import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LegalPersonValidatedFormComponent } from '@app/components/legal-person-validated-form/legal-person-validated-form.component';
import { getLegalEntitiyData } from '@app/components/legal-person-validated-form/legal-person-validated-form.helper';
import { LegalEntityFormGroup } from '@app/models/form.models';
import { LegalEntityRequest } from '@app/models/project-setup.models';
import { LegalEntityDetails } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, switchMap, tap, throwError } from 'rxjs';

type InvestorFormValue = ReturnType<
  FormGroup<LegalEntityFormGroup>['getRawValue']
>;

@Component({
  selector: 'app-details-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    LegalPersonValidatedFormComponent,
  ],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss',
})
export class DetailsTabComponent implements OnInit {
  @Input({ required: true }) investorId!: string;

  investorForm!: FormGroup<LegalEntityFormGroup>;

  private readonly fb = inject(FormBuilder);
  private readonly service = inject(LegalEntityService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private initialValue: InvestorFormValue | null = null;

  ngOnInit(): void {
    this.investorForm = getLegalEntitiyData(this.fb);
    this.load();
  }

  private toFormValue(entity: LegalEntityDetails): InvestorFormValue {
    return {
      taxNumber: entity.taxNumber ?? '',
      name: entity.name ?? '',
      email: entity.email ?? '',
      country: entity.country ?? '',
      ownershipStructure: entity.ownershipStructure ?? null,
      contactInfo: entity.contactInfo ?? '',
      website: entity.website ?? '',
      teaorNumbers: entity.teaorNumbers ?? null,
      bankAccountNumber: entity.bankAccountNumber ?? '',
      companyTypeId: entity.companyType?.id ?? '',
      address: {
        countryCode: entity.address?.countryCode ?? '',
        zipCode: entity.address?.zipCode ?? '',
        city: entity.address?.city ?? '',
        publicSpace: entity.address?.publicSpace ?? '',
        publicSpaceType: entity.address?.publicSpaceType ?? '',
        houseNumber: entity.address?.houseNumber ?? '',
        doorOrFloor: entity.address?.doorOrFloor ?? '',
      },
    };
  }

  private load(): void {
    this.service.getLegalEntityById(this.investorId).subscribe({
      next: (investor) => {
        const formValue = this.toFormValue(investor as LegalEntityDetails);
        this.initialValue = formValue;
        this.investorForm.reset(this.initialValue, { emitEvent: false });
        this.investorForm.markAsPristine();
        this.investorForm.markAsUntouched();
      },
    });
  }

  save(): Observable<void> | null {
    if (!this.investorId) return null;

    this.investorForm.updateValueAndValidity({ emitEvent: false });
    if (this.investorForm.invalid) {
      this.investorForm.markAllAsTouched();
      return null;
    }

    const legalPerson = this.investorForm.getRawValue();
    const payload: LegalEntityRequest = {
      ownershipStructure: legalPerson.ownershipStructure ?? 0,
      contactInfo: legalPerson.contactInfo ?? '',
      website: legalPerson.website ?? '',
      teaorNumbers: legalPerson.teaorNumbers ?? null,
      companyTypeId: legalPerson.companyTypeId ?? '',
      taxNumber: legalPerson.taxNumber,
      name: legalPerson.name,
      email: legalPerson.email,
      country: legalPerson.country,
      bankAccountNumber: legalPerson.bankAccountNumber ?? '',
      address: {
        countryCode: legalPerson.address.countryCode,
        zipCode: legalPerson.address.zipCode,
        city: legalPerson.address.city,
        publicSpace: legalPerson.address.publicSpace,
        publicSpaceType: legalPerson.address.publicSpaceType,
        houseNumber: legalPerson.address.houseNumber,
        doorOrFloor: legalPerson.address.doorOrFloor,
      },
    };

    return this.service.updateLegalEntity(this.investorId, payload).pipe(
      tap(() =>
        this.toast.success(
          this.translate.instant('admin.toasts.savedSuccessfully')
        )
      ),
      switchMap(() => this.service.getLegalEntityById(this.investorId)),
      tap((investor) => {
        const formValue = this.toFormValue(investor as LegalEntityDetails);
        this.initialValue = formValue;
        this.investorForm.reset(formValue, { emitEvent: false });
        this.investorForm.markAsPristine();
        this.investorForm.markAsUntouched();
      }),
      map(() => void 0),
      catchError((err) => {
        this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
        return throwError(() => err);
      })
    );
  }

  cancel(): void {
    this.investorForm.reset(this.initialValue ?? undefined, {
      emitEvent: false,
    });
    this.investorForm.markAsPristine();
    this.investorForm.markAsUntouched();
  }
}
